import { getDefined } from '../defined';
import { getApiUrl } from './getApiUrl';
import { routes } from '../settings';
import { withSafeApi } from './withSafeApi';
import { accessTokenMatchesProject } from '@utils/accessToken';
import { getPersonaGenEndpoint, type PersonaGenApiVersions } from '@utils/url';

import type { CharacterfileSchema } from '@base/config/schema';
import type { ChatMessage } from '@base/pages/agentDraft/useAgentDraftStore';

export type CreateAgentDraftParams = {
  projectId?: string;
  characterfile?: string;
  accessToken?: string;
  name?: string;
  avatar?: string;
  frameworkVersion?: string;
};

export const createAgentDraft = withSafeApi(
  async ({
    projectId,
    characterfile,
    accessToken,
    name,
    avatar,
    frameworkVersion,
  }: CreateAgentDraftParams): Promise<AiAgent> => {
    if (!accessToken || !projectId || !characterfile) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    const url = getApiUrl(routes.endpoints.aiAgentDraftCreate, 2);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        config: characterfile,
        projectId,
        name,
        avatar,
        frameworkVersion,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export const deployAgent = withSafeApi(
  async ({
    projectId,
    characterfile,
    avatar,
    accessToken,
    draftId,
    frameworkVersion,
    tee = false,
  }: {
    projectId?: string;
    characterfile?: string;
    avatar?: string;
    accessToken: string;
    draftId?: string;
    frameworkVersion?: string;
    tee?: boolean;
  }): Promise<string> => {
    if (!accessToken || !projectId || !draftId) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    let name: string | undefined = undefined;
    if (characterfile) {
      const parsedCharacterfile = JSON.parse(characterfile);
      name = parsedCharacterfile.name;
    }
    const url = getApiUrl(routes.endpoints.aiAgentUpdate, 1, {
      pathParams: { id: draftId },
    });

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        config: characterfile,
        projectId,
        name,
        avatar,
        frameworkVersion,
        tee,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    const postUrl = getApiUrl(routes.endpoints.aiAgentPublish, 1, {
      pathParams: { id: draftId },
    });

    const responsePOST = await fetch(postUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!responsePOST.ok) {
      const errorText = await responsePOST.text();
      throw new Error(`HTTP Error ${responsePOST.status}: ${errorText}`);
    }

    return draftId;
  },
);

type DeploymentStepStatus = 'true' | 'false';

export type DeploymentStatus = Record<string, DeploymentStepStatus>;

export type AiAgentStatus =
  | 'Draft'
  | 'Created'
  | 'Pending'
  | 'Running'
  | 'Stopped'
  | 'Failed';

export type AiAgent = {
  avatar?: string;
  status: AiAgentStatus;
  frameworkVersion?: string;
  frameworkName?: string;
  characterFile: string;
  createdAt: string;
  createdBy: string;
  deletedAt?: string;
  host: string;
  id: string;
  name: string;
  projectId: string;
  slotNumber: number;
  updatedAt: string;
  publicToken: string;
  publicChat: boolean;
  tee?: boolean;
};

export const getAgentsByProjectId = withSafeApi(
  async ({
    projectId,
    accessToken,
  }: {
    projectId?: string;
    accessToken?: string;
  }): Promise<AiAgent[]> => {
    if (!accessToken || !projectId) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    const url = getApiUrl(routes.endpoints.aiAgents, 1, {
      queryParams: { projectId },
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return (await response.json()).data;
  },
);

export type GetSubscriptionsRequestProps = {
  projectId?: string;
  accessToken?: string;
};

export type SubscriptionItem = {
  quantity: number;
  id: string;
  metadata?: Record<string, string>;
  productId: string;
  unitAmount: number;
  priceId: string;
  usageType: string;
};

export type Subscription = {
  id: string;
  status: string;
  startDate: string;
  periodStartDate: string;
  periodEndDate: string;
  productId: string;
  endDate?: string | null;
  items: SubscriptionItem[];
  scheduleId: string;
};

export const getSubscriptions = withSafeApi(
  async ({
    projectId,
    accessToken,
  }: GetSubscriptionsRequestProps): Promise<Subscription[]> => {
    if (!projectId || !accessToken) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    const url = getApiUrl(routes.endpoints.subscriptions, 1, {
      queryParams: { projectId },
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok && response.status === 404) {
      return [];
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export type CreateSubscriptionCheckoutRequestProps = {
  projectId?: string;
  productId?: string;
  accessToken?: string;
  referralId?: string;
};

export const createSubscriptionCheckout = withSafeApi(
  async ({
    projectId,
    productId,
    accessToken,
    referralId,
  }: CreateSubscriptionCheckoutRequestProps): Promise<string> => {
    if (!projectId || !accessToken || !productId) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    const url = getApiUrl(routes.endpoints.subscriptionCheckout, 1);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        projectId,
        product: {
          id: productId,
          quantity: 1,
        },
        metadata: {
          referralId,
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return (await response.json()).url;
  },
);

export type GetPlansRequestProps = {
  accessToken?: string;
};

export type Plan = {
  id: string;
  name: string;
  description: string | null;
  price: number;
  createdAt: string;
  updatedAt: string;
  metadata: Record<string, string>;
};

export const getPlans = withSafeApi(
  async ({ accessToken }: GetPlansRequestProps): Promise<Plan[]> => {
    if (!accessToken) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.plans, 1);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export interface GetAgentLogsParams {
  agentId: string;
  accessToken: string;
  pageCursor?: string;
  pageSize?: number;
}

export type AgentLog = {
  id: number;
  timestamp: number;
  message: string;
  priority: number;
};

export type PaginatedResponse<DataType> = {
  data: DataType[];
  next_page: string;
  prev_page: string;
};

export const getAgentLogs = withSafeApi(
  async ({
    agentId,
    accessToken,
    pageCursor,
    pageSize = 25,
  }: GetAgentLogsParams): Promise<PaginatedResponse<AgentLog>> => {
    if (!agentId || !accessToken) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.aiAgentLogs, 1, {
      pathParams: { id: agentId },
      queryParams: {
        cursor: pageCursor,
        size: pageSize,
      },
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export interface GetAgentAttestationParams {
  agentId: string;
}

export type AgentAttestation = Uint8Array<ArrayBufferLike>;

export const getAgentAttestation = withSafeApi(
  async ({ agentId }: GetAgentAttestationParams): Promise<AgentAttestation> => {
    const url = getApiUrl(routes.endpoints.aiAgentAttestation, 1, {
      pathParams: { id: agentId },
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.bytes();
  },
);

export const getAgentStatus = withSafeApi(
  async ({
    accessToken,
    agentId,
  }: {
    accessToken?: string;
    agentId: string;
  }): Promise<'true' | 'false'> => {
    if (!accessToken || !agentId) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.aiAgentStatus, 1, {
      pathParams: { id: agentId },
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return (await response.json()).status;
  },
);

export const updateAgentById = withSafeApi(
  async ({
    accessToken,
    agentId,
    payload,
  }: {
    accessToken?: string;
    agentId: string;
    payload: string;
  }): Promise<AiAgent> => {
    if (!accessToken || !agentId || !payload) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.aiAgentUpdate, 1, {
      pathParams: { id: agentId },
    });

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: payload,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export type ChangeAgentStatusAction = 'start' | 'stop';

export const changeAgentStatus = withSafeApi(
  async ({
    agentId,
    accessToken,
    action,
  }: {
    agentId: string;
    accessToken?: string;
    action: ChangeAgentStatusAction;
  }): Promise<void> => {
    if (!agentId || !accessToken || !action) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.aiAgentStatusUpdate, 1, {
      pathParams: { id: agentId, action },
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }
  },
);

export type DeleteAgentParams = {
  id: string;
  accessToken?: string;
};

export const deleteAgent = withSafeApi(
  async ({ id, accessToken }: DeleteAgentParams): Promise<string> => {
    if (!accessToken || !id) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.aiAgentDelete, 1, {
      pathParams: { id },
    });

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return (await response.json()).id;
  },
);

export type ApiTokenResponse = {
  id: string;
  name: string;
  token_prefix: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  token: string | null;
};

export const getApiTokens = withSafeApi(
  async ({
    accessToken,
  }: {
    accessToken?: string;
  }): Promise<ApiTokenResponse[]> => {
    if (!accessToken) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.tokens, 1);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export type CreateApiTokenPayload = {
  expires_in_days: string | null;
  name: string;
};

export const createApiToken = withSafeApi(
  async ({
    accessToken,
    payload,
  }: {
    accessToken?: string;
    payload: CreateApiTokenPayload;
  }): Promise<ApiTokenResponse> => {
    if (!accessToken || !payload) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.tokens, 1);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export const deleteApiToken = withSafeApi(
  async ({
    accessToken,
    tokenId,
  }: {
    accessToken?: string;
    tokenId: string;
  }): Promise<void> => {
    if (!accessToken || !tokenId) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.tokenDelete, 1, {
      pathParams: { id: tokenId },
    });

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }
  },
);

export type UpdateSubscriptionParams = {
  subscriptionId?: string;
  projectId?: string;
  productId?: string;
  accessToken?: string;
  quantity?: number;
};

export const updateSubscription = withSafeApi(
  async ({
    projectId,
    subscriptionId,
    productId,
    accessToken,
    quantity,
  }: UpdateSubscriptionParams): Promise<Subscription> => {
    if (
      !accessToken ||
      !projectId ||
      !subscriptionId ||
      !productId ||
      quantity === undefined
    ) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    const url = getApiUrl(routes.endpoints.subscriptionUpdate, 1, {
      pathParams: { id: subscriptionId },
    });

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        projectId,
        product: {
          id: productId,
          quantity,
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

interface GetElizaAgentParams {
  agentId?: string;
  accessToken?: string;
  timeout?: number;
}

export interface Agent {
  id: string;
  name: string;
  clients: string[];
}

export interface AgentsData {
  agents: Agent[];
}

export const getElizaAgent = withSafeApi(
  async ({
    agentId,
    accessToken,
    timeout = 30000,
  }: GetElizaAgentParams): Promise<AgentsData> => {
    if (!agentId || !accessToken) {
      throw new Error('Missing required parameters');
    }

    const agentsProxyUrl = getApiUrl(routes.endpoints.proxyAiAgents, 1, {
      pathParams: { id: agentId },
    });

    const responseAgentsProxy = await fetch(agentsProxyUrl, {
      signal: AbortSignal.timeout(timeout),
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!responseAgentsProxy.ok) {
      const errorText = await responseAgentsProxy.text();
      throw new Error(`HTTP Error ${responseAgentsProxy.status}: ${errorText}`);
    }

    return responseAgentsProxy.json();
  },
);

interface SendMessageParams {
  agentId: string;
  agentElizaId: string;
  accessToken?: string;
  message: string;
}

export const sendMessageAgent = withSafeApi(
  async ({
    agentId,
    agentElizaId,
    accessToken,
    message,
  }: SendMessageParams): Promise<
    { user: string; text: string; action: string }[]
  > => {
    if (!agentId || !accessToken || !agentElizaId || !message) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.aiAgentMessage, 1, {
      pathParams: { id: agentId, elizaId: agentElizaId },
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ text: message }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export type ElizaVersionResponse = {
  name: string;
  versions: string[];
};

export const getElizaOsVersions = withSafeApi(
  async ({
    accessToken,
  }: {
    accessToken?: string;
  }): Promise<ElizaVersionResponse> => {
    if (!accessToken) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.elizaOsVersions, 1);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export type Project = {
  id: string;
  name: string;
  avatar: string | null;
};

export const getProjects = withSafeApi(
  async ({ accessToken }: { accessToken?: string }): Promise<Project[]> => {
    if (!accessToken) {
      throw new Error('Missing required parameters');
    }

    const query = `query projects($filter: ProjectsPaginationInput) {
      projects(filter: $filter) {
        data {
          id
          name
          avatar
        }
      }
    }`;

    const response = await fetch(getDefined('PUBLIC_GRAPHQL_API_URL'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        operationName: 'projects',
        query,
        variables: {},
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return (await response.json()).data.projects.data;
  },
);

type GetCreditsRequestProps = {
  accessToken?: string;
  projectId?: string;
};

type Credits = {
  balance: number;
  projectId: string;
  teamId: string;
};

export const getCredits = withSafeApi(
  async ({
    accessToken,
    projectId,
  }: GetCreditsRequestProps): Promise<Credits> => {
    if (!accessToken || !projectId) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.credits, 1, {
      queryParams: { projectId },
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

type CreateCreditsCheckoutProps = {
  accessToken?: string;
  projectId?: string;
};

type CreditsCheckoutResponse = {
  url: string;
};

export const createCreditsCheckout = withSafeApi(
  async ({
    accessToken,
    projectId,
  }: CreateCreditsCheckoutProps): Promise<CreditsCheckoutResponse> => {
    if (!accessToken || !projectId) {
      throw new Error('Missing required parameters');
    }

    const url = getApiUrl(routes.endpoints.createCreditsCheckout, 1);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        projectId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.json();
  },
);

export type CreateSubscriptionRequestProps = {
  projectId?: string;
  productId?: string;
  accessToken?: string;
  referralId?: string;
};

export const createSubscription = withSafeApi(
  async ({
    projectId,
    productId,
    accessToken,
    referralId,
  }: CreateSubscriptionRequestProps): Promise<Subscription> => {
    if (!projectId || !accessToken || !productId) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    const url = getApiUrl(routes.endpoints.subscriptions, 1);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        projectId,
        product: {
          id: productId,
          quantity: 1,
        },
        metadata: {
          referralId,
        },
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return await response.json();
  },
);

export type UpdateSubscriptionQuantityProps = {
  subscriptionId: string;
  projectId?: string;
  accessToken?: string;
  updateBy: number;
};

export const updateSubscriptionQuantity = withSafeApi(
  async ({
    subscriptionId,
    updateBy,
    projectId,
    accessToken,
  }: UpdateSubscriptionQuantityProps): Promise<Subscription> => {
    if (!projectId || !accessToken || !updateBy || !subscriptionId) {
      throw new Error('Missing required parameters');
    }
    if (!accessTokenMatchesProject(accessToken, projectId)) {
      throw new Error('The access token does not match the project');
    }

    const url = getApiUrl(routes.endpoints.subscriptionUpdateQuantity, 1, {
      pathParams: { id: subscriptionId },
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        projectId,
        updateBy,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return await response.json();
  },
);

export const sendAssistantMessageStream = withSafeApi(
  async ({
    content,
    messages,
    accessToken,
  }: {
    content: string;
    messages: ChatMessage[];
    accessToken?: string;
  }) => {
    if (!accessToken || !content) {
      throw new Error('Missing required parameters');
    }

    const url = getPersonaGenEndpoint({
      route: '/assistant/stream',
    });

    const messagesString = messages
      .map((message) => `${message.senderRole}: ${message.content}`)
      .join('\n');

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ content, messages: messagesString }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return response.body?.getReader();
  },
);

export const generateCharacterfile = withSafeApi(
  async ({
    messages,
    accessToken,
    version = 'v1',
  }: {
    messages: unknown[];
    accessToken?: string;
    version?: PersonaGenApiVersions;
  }) => {
    if (!accessToken || !messages) {
      throw new Error('Missing required parameters');
    }

    const url = getPersonaGenEndpoint({
      route: '/generate',
      version,
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ content: JSON.stringify(messages) }),
    });

    const json:
      | {
          apiKey: string;
          baseURL: string;
          data: CharacterfileSchema;
          model: string;
          status: 'success';
        }
      | {
          error: 'string';
          status: 'error';
        } = await response.json();

    if (json.status === 'error') {
      const errorText = json.error;
      throw new Error(`HTTP Error ${response.status}: ${errorText}`);
    }

    return json;
  },
);
